<script setup lang="ts">
import { toRef, computed } from 'vue'

import { LinkedFarmInvitationSentT } from '@ruminati/types_v2/linked_farm_invitation'
import { useResourcePermissions } from '@/composables/permissions'

import NestedRounded from '../Base/NestedRounded.vue'
import { useLinkedFarmInvitationsWithLinkedFarms } from '@/composables/linkedFarmInvitations'
import { formatDateTimestampAsReadable } from '@/utilities/dates'

const props = defineProps<{
  invitation: LinkedFarmInvitationSentT
  setEditInviteId: (id: string) => void
}>()

const { patchInvitation } = useLinkedFarmInvitationsWithLinkedFarms()

const actioned = computed(() => {
  return 'id' in props.invitation.recipientDetails
})

const { allowedToDelete, allowedToEdit } = useResourcePermissions(
  toRef(() => props.invitation.invite.id),
)
</script>

<template>
  <NestedRounded>
    <template #title>
      <p class="markable text-xl">
        {{
          invitation.primaryOrganisation
            ? invitation.primaryOrganisation?.name
            : invitation.recipientDetails.email
        }}
      </p>
    </template>

    <template #inner>
      <a-flex>
        <a-flex v-if="actioned" vertical flex="0 0 25%">
          <p class="text-rumi-grey-600 mb-1 text-sm">Invite Requested by</p>
          <p
            v-if="'firstName' in invitation.recipientDetails"
            class="text-rumi-grey-700 leading-[1.5]"
          >
            <span class="markable"
              >{{ invitation.recipientDetails.firstName }}
              {{ invitation.recipientDetails.lastName }}</span
            >
            <br />
            <span class="markable text-sm">
              {{ invitation.recipientDetails.email }}
            </span>
          </p>
          <p v-else>Not Available</p>
        </a-flex>

        <a-flex vertical flex="1 0 auto" align="flex-end" justify="space-between">
          <p class="text-rumi-grey-600 mb-4 text-sm">
            Requested {{ formatDateTimestampAsReadable(invitation.invite.created) }}
          </p>
          <a-flex justify="flex-end" class="mb-4 w-[160px]">
            <a-button
              danger
              ghost
              block
              :disabled="!allowedToDelete"
              @click="
                () => {
                  patchInvitation(invitation.invite.id, {
                    status: 'archived',
                  })
                }
              "
            >
              Decline
            </a-button>
          </a-flex>
          <a-flex justify="flex-end" class="w-[160px]">
            <a-button
              type="primary"
              ghost
              block
              :disabled="!allowedToEdit"
              @click="
                () => {
                  patchInvitation(invitation.invite.id, {
                    status: 'invited',
                  })
                }
              "
            >
              Accept
            </a-button>
          </a-flex>
        </a-flex>
      </a-flex>
    </template>
  </NestedRounded>
</template>
